// Reine Übersetzung zwischen React-Flow-Zustand und dem Signalgraph-JSON (graph.schema.json).
//
// Diese Funktionen halten keine React-Abhängigkeit und sind damit unmittelbar testbar. Sie sind
// der Kern der Signalgraph-Ansicht: aus platzierten Knoten und gezogenen Kanten wird ein
// schema-konformes graph.json (schema_version, nodes[], edges[] mit from/to {node,port}).

import type { Edge, Node } from '@xyflow/react';
import type { GraphEdge, GraphNode, SignalGraph } from '../api/types';

// Nutzlast, die ein Signalgraph-Knoten auf der Canvas trägt. `blockType` ist der schema-`type`
// (boris2py-Blocktyp oder 'catalog-input'/'catalog-output'/'model-inference').
export interface GraphNodeData extends Record<string, unknown> {
  blockType: string;
  label: string;
  params: Record<string, unknown>;
  inputs: number[]; // 1-indizierte Eingangsports
  outputs: number[]; // 1-indizierte Ausgangsports
  // Nur für superblock-Knoten (Issue #271): true, wenn die referenzierte .SBL weder als Default
  // noch als Import in der Bibliothek liegt — der Knoten wird dann auf der Canvas markiert.
  unknownSuperblock?: boolean;
  // Signalart der gebundenen Katalog-Variable (Issue #352), nur für katalog-gebundene Knoten. Sie
  // steht NICHT im Graphen — der kennt nur den Tag — sondern wird beim Zeichnen aus dem Katalog
  // nachgeschlagen, damit ein exogenes Signal auf der Canvas als solches erkennbar ist.
  signalKind?: string;
}

export type FlowGraphNode = Node<GraphNodeData>;
export type FlowGraphEdge = Edge;

// React-Flow-Handle-IDs kodieren die 1-indizierte Portnummer als 'in-<n>' bzw. 'out-<n>'.
export function inHandle(port: number): string {
  return `in-${port}`;
}
export function outHandle(port: number): string {
  return `out-${port}`;
}

// Zerlegt eine Handle-ID wieder in die 1-indizierte Portnummer. Fällt fail-safe auf Port 1
// zurück (der einzige Port der catalog-Knoten), falls kein Handle gesetzt war.
export function portFromHandle(handle: string | null | undefined): number {
  if (!handle) return 1;
  const n = Number.parseInt(handle.replace(/^(in|out)-/, ''), 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

// Baut aus dem React-Flow-Zustand das schema-konforme Signalgraph-JSON. `meta` optional
// (Projektbezug, Zykluszeit). Positionen werden mitgeschrieben, damit ein erneutes Laden das
// Layout erhält.
export function buildGraph(
  nodes: FlowGraphNode[],
  edges: FlowGraphEdge[],
  meta?: SignalGraph['meta'],
): SignalGraph {
  const graphNodes: GraphNode[] = nodes.map((n) => {
    const node: GraphNode = {
      id: n.id,
      type: n.data.blockType,
      params: n.data.params ?? {},
      position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
    };
    if (n.data.label) node.label = n.data.label;
    return node;
  });

  const graphEdges: GraphEdge[] = edges.map((e) => ({
    id: e.id,
    from: { node: e.source, port: portFromHandle(e.sourceHandle) },
    to: { node: e.target, port: portFromHandle(e.targetHandle) },
  }));

  const graph: SignalGraph = {
    schema_version: 1,
    nodes: graphNodes,
    edges: graphEdges,
  };
  if (meta && Object.keys(meta).length > 0) graph.meta = meta;
  return graph;
}

// Rückweg: aus einem geladenen Signalgraph-JSON den React-Flow-Zustand herstellen. `portsOf`
// liefert die Ein-/Ausgangsports je Knotentyp (aus der Palette bzw. für die Sonder-Knoten fest).
// `layout` ist eine optionale, vorab berechnete Positionskarte (Auto-Layout): trägt ein Knoten
// keine eigene Position, wird die berechnete verwendet, sonst das Gitter (`fallbackPosition`).
export function graphToFlow(
  graph: SignalGraph,
  portsOf: (type: string) => { inputs: number[]; outputs: number[] },
  fallbackPosition: (index: number) => { x: number; y: number },
  layout?: Map<string, { x: number; y: number }>,
): { nodes: FlowGraphNode[]; edges: FlowGraphEdge[] } {
  // Tatsächlich per Kante belegte Ports je Knoten sammeln. Nötig für Blöcke mit VARIABLER
  // Portzahl (DEQSYS/MULTIPLOT2/FCTN/JUNCTION) und importierte BORIS-Knoten: deren Palette-Typ
  // liefert keine feste Portzahl (`None`), sodass `portsOf` leer ist. Ohne Handle am belegten
  // Port würde React-Flow die (vorhandene) Kante nicht zeichnen — der Knoten wirkt „lose".
  const usedIn = new Map<string, Set<number>>();
  const usedOut = new Map<string, Set<number>>();
  const mark = (m: Map<string, Set<number>>, id: string, port: number): void => {
    let s = m.get(id);
    if (!s) {
      s = new Set();
      m.set(id, s);
    }
    s.add(port);
  };
  for (const e of graph.edges) {
    mark(usedOut, e.from.node, e.from.port);
    mark(usedIn, e.to.node, e.to.port);
  }
  const unionPorts = (declared: number[], used: Set<number> | undefined): number[] =>
    Array.from(new Set([...declared, ...(used ?? [])])).sort((a, b) => a - b);

  const nodes: FlowGraphNode[] = graph.nodes.map((n, i) => {
    const ports = portsOf(n.type);
    const position = n.position ?? layout?.get(n.id) ?? fallbackPosition(i);
    return {
      id: n.id,
      type: 'signal',
      position,
      data: {
        blockType: n.type,
        label: n.label ?? n.type,
        params: n.params ?? {},
        inputs: unionPorts(ports.inputs, usedIn.get(n.id)),
        outputs: unionPorts(ports.outputs, usedOut.get(n.id)),
      },
    };
  });

  const edges: FlowGraphEdge[] = graph.edges.map((e, i) => ({
    id: e.id ?? `e-${i + 1}`,
    source: e.from.node,
    sourceHandle: outHandle(e.from.port),
    target: e.to.node,
    targetHandle: inHandle(e.to.port),
  }));

  return { nodes, edges };
}

// Sonder-Knoten-Ports: catalog-input hat genau einen Ausgang (Port 1), catalog-output genau
// einen Eingang, signal-filter je einen (er filtert, was anliegt), model-inference als
// Platzhalter je einen Ein-/Ausgang.
export function specialNodePorts(type: string): { inputs: number[]; outputs: number[] } | null {
  switch (type) {
    case 'catalog-input':
      return { inputs: [], outputs: [1] };
    case 'catalog-output':
      return { inputs: [1], outputs: [] };
    case 'signal-filter':
      return { inputs: [1], outputs: [1] };
    case 'model-inference':
      return { inputs: [1], outputs: [1] };
    // mpc-optimizer: die echte Portzahl folgt erst beim Bau aus params.manipulated (je
    // Stellgröße ein Vorschlag plus der Kostenwert). Im Editor je ein Platzhalter, wie beim
    // Modellknoten — die Bindung setzt die Zahl, nicht die Palette.
    case 'mpc-optimizer':
      return { inputs: [1], outputs: [1] };
    default:
      return null;
  }
}
